import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import * as constants from "@redbubble/design-system/react/constants";
import Avatar from "@redbubble/design-system/react/Avatar";
import Box from "@redbubble/design-system/react/Box";
import Button from "@redbubble/design-system/react/Button";
import Form, { Field } from "@redbubble/design-system/react/Form";
import Text from "@redbubble/design-system/react/Text";
import messages from "./messages";
import styles from "./CommentsSection.scss";
import { analyticsPayload } from "../../../../../lib/analytics";
import { useAddComment } from "../../../../../containers/apollo/useAddComment";
import ErrorMessage from "./ErrorMessage";

const NewComment = (props) => {
  const {
    avatar,
    commentsCount,
    displayName,
    isArtist,
    logEvent,
    parentId,
    repliesCount,
    setReplyToCommentId,
    userAccountLoading,
    userInfoLoading,
    workId,
  } = props;

  const { addComment, addCommentLoading, addCommentError } = useAddComment();

  const submitForm = async ({ comment }, { resetForm }) => {
    const variables = {
      comment,
      parentId,
      workId,
    };

    await addComment(variables);

    if (parentId) {
      logEvent({
        analytics: analyticsPayload.artistWorkReplyToComment(
          `${workId}_${commentsCount}_${repliesCount + 1}`
        ),
      });
    } else {
      logEvent({
        analytics: analyticsPayload.artistWorkAddComment(
          `${workId}_${commentsCount + 1}`
        ),
      });
    }

    setReplyToCommentId(null);
    resetForm();
  };

  return (
    <Box className={styles.newComment}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        paddingLeft="xs"
        paddingBottom="xs"
      >
        {avatar && (
          <Avatar
            src={avatar}
            alt={displayName}
            size={constants.TINY}
            loading={userInfoLoading}
            loadOnVisible
          />
        )}
        <Box marginLeft={avatar ? 0.5 : 0}>
          {userAccountLoading ? (
            <Text type="display5" loading>
              displayName
            </Text>
          ) : (
            <Text type="display5">{displayName}</Text>
          )}
        </Box>
        {isArtist && (
          <Box marginLeft="xs">
            <Text type="caption" muted>
              <FormattedMessage {...messages.artistCaption} />
            </Text>
          </Box>
        )}
      </Box>
      <Form
        initialValues={{
          comment: "",
        }}
        onSubmit={submitForm}
      >
        {(form) => (
          <>
            <FormattedMessage {...messages.leaveAComment}>
              {(text) => (
                <Field
                  label="comment"
                  hideLabel
                  type="textarea"
                  name="comment"
                  placeholder={text}
                  fluid
                  rows={form.values.comment ? "2" : "1"}
                  resizable
                  disabled={userInfoLoading || addCommentLoading}
                />
              )}
            </FormattedMessage>
            {form.values.comment && (
              <Box
                marginTop="xs"
                marginRight="xs"
                style={{ textAlign: "right" }}
              >
                <Button
                  intent={constants.PRIMARY}
                  type="submit"
                  strong
                  loading={addCommentLoading}
                >
                  <FormattedMessage {...messages.addCommentButton} />
                </Button>
              </Box>
            )}
          </>
        )}
      </Form>
      {addCommentError && (
        <ErrorMessage message={messages.newCommentErrorMessage} />
      )}
    </Box>
  );
};

NewComment.propTypes = {
  avatar: PropTypes.string,
  commentsCount: PropTypes.number,
  displayName: PropTypes.string,
  isArtist: PropTypes.bool,
  logEvent: PropTypes.func,
  parentId: PropTypes.string,
  repliesCount: PropTypes.number,
  setReplyToCommentId: PropTypes.func,
  userAccountLoading: PropTypes.bool,
  userInfoLoading: PropTypes.bool,
  workId: PropTypes.string.isRequired,
};

NewComment.defaultProps = {
  avatar: "",
  commentsCount: 0,
  displayName: "",
  isArtist: false,
  logEvent: () => {},
  parentId: null,
  repliesCount: 0,
  setReplyToCommentId: () => {},
  userAccountLoading: true,
  userInfoLoading: true,
};

export default NewComment;
